import { memo, useState, useRef, useEffect, useCallback } from 'react';
import styles from './MultipleDropdown.module.css';

// Numeric-only custom multiple: digits with an optional single decimal point
// and at most two decimal places. Rejects a thousands comma (or any
// separator) so the field can never hold "1,5", and any non-numeric text. A
// lone "" or "." matches as a valid in-progress edit but isn't a usable
// number — the commit logic falls back in that case (see MultipleDropdown's
// close handler). Module-scoped so it's a stable reference.
const CUSTOM_INPUT_PATTERN = /^\d*\.?\d{0,2}$/;

/**
 * The main chart's fair-value multiple, as a dropdown next to the metric
 * dropdown (replacing the old always-visible free-text input).
 *
 * Two modes:
 *   - `default`: use the hardcoded per-metric/anchor multiple (passed in as
 *     `defaultValue`). No editable field — it tracks whatever metric/anchor
 *     is selected.
 *   - `custom`: the user's own multiple, entered in a revealed number input.
 *
 * Commit-on-collapse: unlike the other live controls (which apply on each
 * click), this defers — the chart only re-plots when the dropdown CLOSES, via
 * `onCommit(mode, value)`. That keeps the custom field from re-plotting on
 * every keystroke without needing a debounce, and matches the "select, then
 * close to apply" mental model. Selecting Default and closing commits the
 * default; selecting Custom, typing, and closing commits the typed value.
 *
 * On close with an empty / "." / non-positive custom value, the committed
 * value falls back to the last usable custom value, or `defaultValue` if none
 * was ever entered — so the chart never plots a zero/blank fair-value line.
 *
 * The closed trigger shows the current selection: "Default" or
 * "Custom: <value>".
 */
const MultipleDropdown = ({ mode, value, defaultValue, onCommit }) => {
  const [open, setOpen] = useState(false);
  // Local, uncommitted edit state while the panel is open. Seeded from props
  // when the panel opens, read back out on close.
  const [draftMode, setDraftMode] = useState(mode);
  const [draftCustom, setDraftCustom] = useState(
    mode === 'custom' && value != null ? String(value) : '',
  );
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Resolve the draft into a committed (mode, value) pair, applying the
  // empty/invalid fallback for custom, then notify the parent — but only if
  // something actually changed, so merely opening+closing the panel doesn't
  // trigger a redundant rebuild.
  const commit = useCallback(() => {
    if (draftMode === 'default') {
      if (mode !== 'default') onCommit('default', defaultValue);
      return;
    }
    const parsed = Number(draftCustom);
    const usable = draftCustom !== '' && draftCustom !== '.' && Number.isFinite(parsed) && parsed > 0;
    // Fall back to the last committed custom value, or the metric default if
    // there wasn't one, when the field is left empty/invalid on close.
    const committedValue = usable
      ? parsed
      : (mode === 'custom' && value != null ? value : defaultValue);
    if (mode !== 'custom' || committedValue !== value) {
      onCommit('custom', committedValue);
    }
  }, [draftMode, draftCustom, mode, value, defaultValue, onCommit]);

  const close = useCallback(() => {
    setOpen((isOpen) => {
      if (isOpen) commit();
      return false;
    });
  }, [commit]);

  const toggle = useCallback(() => {
    if (open) {
      close();
    } else {
      // Re-seed the draft from the current committed props each time the panel
      // opens, so it reflects any metric-driven default change since last time.
      setDraftMode(mode);
      setDraftCustom(mode === 'custom' && value != null ? String(value) : '');
      setOpen(true);
    }
  }, [open, close, mode, value]);

  // Close (and commit) on an outside click or Escape while open.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        close();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // Focus the custom field as soon as Custom is chosen (or the panel opens
  // already in custom mode), so the user can type immediately.
  useEffect(() => {
    if (open && draftMode === 'custom') inputRef.current?.focus();
  }, [open, draftMode]);

  const handleCustomInput = useCallback((event) => {
    const raw = event.target.value;
    if (!CUSTOM_INPUT_PATTERN.test(raw)) return; // reject the keystroke
    setDraftCustom(raw);
  }, []);

  // The closed trigger just shows the effective numeric multiple (e.g. "15"),
  // not the mode — the "× <value>" framing next to the metric dropdown makes
  // its meaning clear, and Default vs Custom is only a distinction while the
  // panel is open. Falls back to the metric default if a custom value is
  // somehow absent.
  const triggerLabel = value != null ? String(value) : String(defaultValue);

  return (
    <div className={styles.dropdown} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Fair-value multiple"
      >
        {triggerLabel}
      </button>

      {open && (
        <div className={styles.panel} role="listbox">
          <button
            type="button"
            role="option"
            aria-selected={draftMode === 'default'}
            className={`${styles.option} ${draftMode === 'default' ? styles.optionActive : ''}`}
            onClick={() => setDraftMode('default')}
          >
            Default
          </button>
          <button
            type="button"
            role="option"
            aria-selected={draftMode === 'custom'}
            className={`${styles.option} ${draftMode === 'custom' ? styles.optionActive : ''}`}
            onClick={() => setDraftMode('custom')}
          >
            Custom
          </button>

          {draftMode === 'custom' && (
            <div className={styles.customRow}>
              <input
                ref={inputRef}
                type="text"
                className={styles.customInput}
                value={draftCustom}
                onChange={handleCustomInput}
                onKeyDown={(event) => {
                  // Enter commits + closes, matching the "close to apply" model.
                  if (event.key === 'Enter') close();
                }}
                placeholder={`Enter multiple (default ${defaultValue})`}
                autoComplete="off"
                inputMode="decimal"
                aria-label="Custom multiple value"
              />
              {/* Explicit confirm: same path as Enter/close — commits the
                  typed value (triggering the re-plot) and closes the panel. */}
              <button
                type="button"
                className={styles.confirm}
                onClick={close}
                aria-label="Apply custom multiple"
              >
                ✓
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(MultipleDropdown);
